import type { DifficultyProfile } from '../config/difficulty';
import { TUNING } from '../config/tuning';
import type { AIGameState, AIIntent, IAIController, QteBotContext } from './AIController';
import { LungController } from './LungController';

type SubState = 'idle' | 'thinking' | 'inhaling' | 'combo-window';

const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

const gaussian = (sigma: number): number => {
  const u1 = Math.max(1e-9, Math.random());
  const u2 = Math.random();
  return sigma * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
};

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/**
 * NarcoPsyche — the feelings layer on top of Narco's EV planner.
 *
 * The numbers themselves are planner-internal only. They modulate what the
 * planner "wants" each tick without changing the planner's structure:
 *
 * - confidence (0..1, start 0.5): rises on clean drags & QTE wins, falls on
 *   chokes. Nudges the choke ceiling up and the EV release bias down when
 *   high — the bot holds longer when it feels hot.
 *
 * - revengeTimerMs: set when the player's score suddenly spikes or the bot
 *   just ate a big choke. While > 0, the bot plays aggressive (bias down).
 *
 * - cautionTimerMs: set immediately after a bot choke. While > 0, the bot
 *   trims the ceiling and shortens drags — "licking wounds" for one turn.
 *
 * - slipsBudget / slipProbability: occasional intentional tactical mistakes
 *   (hold a tiny bit too long) so Narco feels human instead of optimal.
 */
interface NarcoPsyche {
  confidence: number;
  revengeTimerMs: number;
  cautionTimerMs: number;
  slipsBudget: number;
  lastSeenOpponentScore: number;
  /** A slip defers a release by this many ms. 0 = no active slip. */
  pendingSlipDeferMs: number;
}

const PSYCHE_TUNING = {
  confidenceGainPerQteWin: 0.15,
  confidenceGainPerLongDrag: 0.08,  // drag >= 3s
  longDragThresholdSecs: 3,
  confidenceLossPerChoke: 0.40,
  confidenceDecayPerTurn: 0.9,      // multiplier applied on beginTurn
  confidenceInitial: 0.5,
  confidenceCeilingBonusMax: 8,     // +0..+8% to choke ceiling at full confidence
  confidenceBiasMax: 0.30,          // 0..0.30 subtracted from release threshold when confident
  // Revenge
  revengeDurationMs: 8500,          // ~2 Narco turns worth
  revengeBiasBonus: 0.5,            // subtract from release threshold
  revengeCeilingBonus: 12,          // +12% ceiling
  revengeOpponentSpikeSecs: 4,      // trigger if opponent gained this much in one turn
  // Caution (post-choke)
  cautionDurationMs: 5500,          // ~1 Narco turn worth
  cautionCeilingPenalty: 15,        // -15% ceiling
  cautionComboCapPenalty: 2,        // -2 from soft combo cap
  // Close-game tension
  closeGameDeltaSecs: 3,            // |player - ai| < 3s AND joint < 60%
  closeGameJointPct: 60,
  closeGameSigmaDivisor: 0.6,       // QTE sigma *= this (smaller sigma = more accurate)
  // Tactical slips
  slipsPerMatch: 3,
  slipProbability: 0.12,
  slipDeferMinMs: 150,
  slipDeferMaxMs: 400,
};

/**
 * NARCO — the master stoner.
 *
 * Plays with a mental model of the full game state AND a psyche layer that
 * gives it momentum, grudges, and the occasional human slip.
 *
 * Base planner:
 * - Computes expected value of continuing each inhale tick vs releasing now,
 *   accounting for the fact that a choke wipes the ENTIRE turn's combo.
 * - Adjusts its target choke-meter ceiling based on score lead: pushes harder
 *   when behind, plays safer when ahead.
 * - Plans combos by projecting "will my next drag clear the 0.4s minimum
 *   given lung headroom AND choke headroom?" — only continues if yes.
 * - Respects the joint-length → lung-drain ramp explicitly.
 *
 * Psyche overlay (see NarcoPsyche above):
 * - Confidence streak: clean drags + QTE wins → holds longer, ceiling rises.
 * - Revenge mode: opponent scoring spike or bot-choke → short burst of
 *   aggression regardless of score state.
 * - Caution after choke: trims ceiling + combo cap for ~1 turn, overlapping
 *   with revenge produces "angry but burned" play — short aggressive drags.
 * - Close-game tension: tightens QTE aim when game is within 3s AND joint <60%.
 * - Tactical slips: 12% chance to delay a release by 150–400ms; limited to
 *   3 per match. Sells the "human who lost track for a beat" feel.
 */
export class NarcoAIController implements IAIController {
  private profile: DifficultyProfile;
  private subState: SubState = 'idle';
  private waitMs = 0;
  private currentDragSecs = 0;
  private combosTaken = 0;
  /** Extra combo slots granted by successful QTEs — additive to the base cap. */
  private comboBonus = 0;
  /** Narco always engages QTEs when armed (planner handles the plan). */
  private qteSeeking = false;
  private qteReleaseLungPct: number | null = null;

  private psyche: NarcoPsyche = {
    confidence: PSYCHE_TUNING.confidenceInitial,
    revengeTimerMs: 0,
    cautionTimerMs: 0,
    slipsBudget: PSYCHE_TUNING.slipsPerMatch,
    lastSeenOpponentScore: 0,
    pendingSlipDeferMs: 0,
  };

  constructor(profile: DifficultyProfile) {
    this.profile = profile;
  }

  setProfile(p: DifficultyProfile): void {
    this.profile = p;
  }

  beginTurn(): void {
    this.subState = 'thinking';
    this.waitMs = rand(80, 180);
    this.currentDragSecs = 0;
    this.combosTaken = 0;
    this.comboBonus = 0;
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
    // Psyche: confidence decays a bit each turn (prevents a permanent max),
    // and slip defer resets. Timers tick down via ms elsewhere.
    this.psyche.confidence *= PSYCHE_TUNING.confidenceDecayPerTurn;
    this.psyche.pendingSlipDeferMs = 0;
  }

  onQteResolved(result: { won: boolean; chokePctAfter: number; pctReduced: number }): void {
    if (result.won) {
      this.psyche.confidence = clamp(
        this.psyche.confidence + PSYCHE_TUNING.confidenceGainPerQteWin,
        0, 1,
      );
      // Comfortable post-QTE state → grant combo bonus (existing behaviour).
      if (result.chokePctAfter < 85) {
        this.comboBonus += result.chokePctAfter < 60 ? 2 : 1;
      }
    }
    // On miss there's no direct psyche hit — just no gain.
  }

  endTurn(): void {
    this.subState = 'idle';
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
  }

  notifyInhaleStarted(): void {
    this.subState = 'inhaling';
    this.currentDragSecs = 0;
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
    // Each new inhale gets a fresh slip-attempt roll (evaluated on release).
    this.psyche.pendingSlipDeferMs = 0;
  }

  notifyInhaleEnded(reason?: 'released' | 'choked' | 'joint-burned' | 'lung-empty'): void {
    this.qteSeeking = false;
    this.qteReleaseLungPct = null;
    this.combosTaken += 1;
    this.subState = 'combo-window';
    this.waitMs = rand(200, 400);

    // Psyche updates from the inhale outcome.
    if (reason === 'released') {
      if (this.currentDragSecs >= PSYCHE_TUNING.longDragThresholdSecs) {
        this.psyche.confidence = clamp(
          this.psyche.confidence + PSYCHE_TUNING.confidenceGainPerLongDrag,
          0, 1,
        );
      }
    } else if (reason === 'choked' || reason === 'lung-empty' || reason === 'joint-burned') {
      // Any forfeit → caution kicks in + confidence takes a hit + revenge arms.
      this.psyche.cautionTimerMs = PSYCHE_TUNING.cautionDurationMs;
      this.psyche.confidence = clamp(
        this.psyche.confidence - PSYCHE_TUNING.confidenceLossPerChoke,
        0, 1,
      );
      this.psyche.revengeTimerMs = PSYCHE_TUNING.revengeDurationMs;
    }
  }

  onQteArmed(): void {
    // Narco always attempts — the planner commits to reaching the window.
    this.qteSeeking = true;
  }

  scheduleQteRelease(
    ctx: QteBotContext,
    _durationMs: number,
    goldenStartPct: number,
    goldenEndPct: number,
  ): void {
    // Line is now lung-driven (see QteController.getLinePct). Aim for the lung
    // level that puts the line at golden center, with sigma expressed in
    // lung-% (tight under pressure, loose when comfortable).
    let sigmaLung = 3.5;
    if (ctx.chokePct < 30) sigmaLung = 5.5;
    else if (ctx.chokePct > 70) sigmaLung = 2.0;
    if (ctx.jointPct <= TUNING.joint.cardboardZonePct) sigmaLung = 1.2; // desperate
    // Psyche: close-game tension sharpens aim.
    // (We don't have full state here, but jointPct is in ctx; score-closeness
    // is approximated by confidence: high confidence + mid-choke → still loose,
    // low confidence + mid-choke → already tight because of chokePct above.)
    // Apply the close-game sharpening when joint is deep into second half.
    if (ctx.jointPct < PSYCHE_TUNING.closeGameJointPct) {
      sigmaLung *= PSYCHE_TUNING.closeGameSigmaDivisor;
    }
    const trigger = TUNING.qte.triggerLungPct;
    const end = TUNING.qte.sweepEndLungPct;
    const span = trigger - end;
    const goldenCenter = (goldenStartPct + goldenEndPct) / 2;
    const idealLung = end + goldenCenter * span;
    const halfZone = (TUNING.qte.goldenZoneWidthPct / 100) * span * 0.5;
    const jitter = gaussian(sigmaLung);
    const clamped = Math.max(-halfZone * 2, Math.min(halfZone * 2, jitter));
    this.qteReleaseLungPct = idealLung + clamped;
  }

  update(deltaMs: number, state: AIGameState): AIIntent {
    this.tickPsyche(deltaMs, state);

    if (this.subState === 'idle') return 'pass';

    if (this.subState === 'thinking') {
      this.waitMs -= deltaMs;
      if (this.waitMs <= 0 && state.lungPct > 30) return 'start-inhale';
      return 'wait';
    }

    if (this.subState === 'inhaling') {
      this.currentDragSecs += deltaMs / 1000;

      // QTE-scheduled release overrides planner; hard safety still applies.
      if (this.qteReleaseLungPct !== null) {
        if (state.lungPct <= 3) { this.qteReleaseLungPct = null; return 'end-inhale'; }
        if (state.jointPct <= 1.2) { this.qteReleaseLungPct = null; return 'end-inhale'; }
        if (state.chokePct >= 99) { this.qteReleaseLungPct = null; return 'end-inhale'; }
        if (state.lungPct <= this.qteReleaseLungPct) {
          this.qteReleaseLungPct = null;
          return 'end-inhale';
        }
        return 'wait';
      }

      // QTE-seeking: reach the window with relaxed stops.
      if (this.qteSeeking) {
        if (this.lungSecondsUntilFloor(state, 5) <= 0.08) return 'end-inhale';
        if (state.jointPct <= 1.2) return 'end-inhale';
        if (state.chokePct >= this.profile.chokeMeterCeiling + 7) return 'end-inhale';
        return 'wait';
      }

      return this.maybeSlip(this.evaluateInhale(state), deltaMs);
    }

    if (this.subState === 'combo-window') {
      this.waitMs -= deltaMs;
      if (this.waitMs > 0) return 'wait';
      return this.evaluateCombo(state);
    }

    return 'wait';
  }

  // --- Psyche tick ---

  private tickPsyche(deltaMs: number, state: AIGameState): void {
    if (this.psyche.revengeTimerMs > 0) {
      this.psyche.revengeTimerMs = Math.max(0, this.psyche.revengeTimerMs - deltaMs);
    }
    if (this.psyche.cautionTimerMs > 0) {
      this.psyche.cautionTimerMs = Math.max(0, this.psyche.cautionTimerMs - deltaMs);
    }

    // Revenge trigger: opponent score spiked by >= threshold in one update.
    // We seed `lastSeenOpponentScore` on first tick, then diff.
    if (this.psyche.lastSeenOpponentScore === 0 && state.opponentScore > 0) {
      this.psyche.lastSeenOpponentScore = state.opponentScore;
    }
    const spike = state.opponentScore - this.psyche.lastSeenOpponentScore;
    if (spike >= PSYCHE_TUNING.revengeOpponentSpikeSecs) {
      this.psyche.revengeTimerMs = PSYCHE_TUNING.revengeDurationMs;
      this.psyche.lastSeenOpponentScore = state.opponentScore;
    } else if (spike !== 0) {
      // Small changes — just update the baseline so the next spike is measured
      // against the most recent rest point.
      this.psyche.lastSeenOpponentScore = state.opponentScore;
    }
  }

  // --- Brain ---

  /** Lead AFTER the current turn's combo is banked. Positive = Narco ahead. */
  private projectedLead(state: AIGameState): number {
    return (state.myScore + state.turnBankedSecs + this.currentDragSecs) - state.opponentScore;
  }

  /** How many more seconds of inhale the lungs allow before hitting a safety floor. */
  private lungSecondsUntilFloor(state: AIGameState, floorPct: number): number {
    const mult = LungController.drainMultiplierForJointPct(state.jointPct);
    const headroom = state.lungPct - floorPct;
    if (headroom <= 0) return 0;
    return headroom / (TUNING.lung.inhaleDepletionPctPerSec * mult);
  }

  /** How many more seconds of inhale before the choke meter reaches `targetMeter`. */
  private chokeSecondsUntilMeter(currentMeter: number, targetMeter: number): number {
    const delta = targetMeter - currentMeter;
    if (delta <= 0) return 0;
    return delta / TUNING.choke.risePerSec;
  }

  /**
   * Target choke ceiling (hard release point) for current score state AND
   * psyche. Applies confidence bonus + revenge bonus − caution penalty.
   */
  private targetChokeMeterFor(state: AIGameState): {
    target: number;
    desperate: boolean;
    dominating: boolean;
    revenge: boolean;
    caution: boolean;
  } {
    const lead = this.projectedLead(state);
    const desperate = lead < -2;
    const dominating = lead > 8;
    let target = 75;
    if (desperate) target = 88;
    else if (dominating) target = 50;
    // Psyche adjustments.
    target += this.psyche.confidence * PSYCHE_TUNING.confidenceCeilingBonusMax;
    const revenge = this.psyche.revengeTimerMs > 0;
    const caution = this.psyche.cautionTimerMs > 0;
    if (revenge) target += PSYCHE_TUNING.revengeCeilingBonus;
    if (caution) target -= PSYCHE_TUNING.cautionCeilingPenalty;
    // Never exceed the absolute profile ceiling.
    target = Math.min(target, this.profile.chokeMeterCeiling);
    return { target, desperate, dominating, revenge, caution };
  }

  private evaluateInhale(state: AIGameState): AIIntent {
    const { target, desperate, dominating, revenge } = this.targetChokeMeterFor(state);
    const atRisk = state.turnBankedSecs + this.currentDragSecs;

    // --- Hard stops ---
    if (this.lungSecondsUntilFloor(state, 6) <= 0.08) return 'end-inhale';
    if (state.jointPct <= 3 && !desperate) return 'end-inhale';
    if (state.jointPct <= 1.2) return 'end-inhale';
    if (state.chokePct >= this.profile.chokeMeterCeiling) return 'end-inhale';
    if (state.chokePct >= target) return 'end-inhale';

    // --- Expected-value safety net ---
    const chokeProbPerSec = this.chokeProbPerSec(state.chokePct);
    const expectedSecLost = (chokeProbPerSec / 100) * Math.max(0.5, atRisk);
    const expectedSecGained = 1;
    let bias = 0;
    if (desperate) bias = 0.45;
    else if (dominating) bias = -0.7;
    // Psyche bias stacking.
    bias += this.psyche.confidence * PSYCHE_TUNING.confidenceBiasMax;
    if (revenge) bias += PSYCHE_TUNING.revengeBiasBonus;
    // Close-game tension: tighten bias toward zero (no reckless extensions).
    const leadAbs = Math.abs(this.projectedLead(state));
    const closeGame = leadAbs < PSYCHE_TUNING.closeGameDeltaSecs
      && state.jointPct < PSYCHE_TUNING.closeGameJointPct;
    if (closeGame) bias = bias * 0.6; // dampen both positive and negative extremes

    if (expectedSecLost - bias > expectedSecGained * 0.7) return 'end-inhale';

    return 'wait';
  }

  private evaluateCombo(state: AIGameState): AIIntent {
    const { target, desperate, dominating, caution } = this.targetChokeMeterFor(state);

    const chokeHeadroomSecs = this.chokeSecondsUntilMeter(state.chokePct, target);
    const lungHeadroomSecs = this.lungSecondsUntilFloor(state, 12);
    const estimatedNextDrag = Math.min(chokeHeadroomSecs, lungHeadroomSecs);

    // Cardboard zone.
    if (state.jointPct <= 5) {
      if (desperate && estimatedNextDrag > 0.3) return 'start-inhale';
      return 'pass';
    }

    // Not worth the risk unless we can get a meaningful drag out.
    if (estimatedNextDrag < 0.4) return 'pass';

    // Soft combo cap, reduced when Narco is in post-choke caution.
    let maxCombos = (desperate ? 10 : (dominating ? 3 : 7)) + this.comboBonus;
    if (caution) maxCombos -= PSYCHE_TUNING.cautionComboCapPenalty;
    if (this.combosTaken >= maxCombos) return 'pass';

    return 'start-inhale';
  }

  /**
   * Tactical slip: occasionally defer a release by 150–400ms so Narco looks
   * like a human who held a beat too long. Budgeted per match so it doesn't
   * feel sloppy. Only acts on 'end-inhale' intents — never holds past a hard
   * stop (those came from evaluateInhale's safety checks above).
   */
  private maybeSlip(intent: AIIntent, deltaMs: number): AIIntent {
    if (intent !== 'end-inhale') return intent;

    // If a slip is already active, keep deferring until the timer burns out.
    if (this.psyche.pendingSlipDeferMs > 0) {
      this.psyche.pendingSlipDeferMs = Math.max(0, this.psyche.pendingSlipDeferMs - deltaMs);
      if (this.psyche.pendingSlipDeferMs > 0) return 'wait';
      return 'end-inhale';
    }

    // Roll for a new slip.
    if (this.psyche.slipsBudget <= 0) return intent;
    if (Math.random() > PSYCHE_TUNING.slipProbability) return intent;

    this.psyche.slipsBudget -= 1;
    this.psyche.pendingSlipDeferMs = rand(
      PSYCHE_TUNING.slipDeferMinMs,
      PSYCHE_TUNING.slipDeferMaxMs,
    );
    return 'wait';
  }

  private chokeProbPerSec(meter: number): number {
    const t = TUNING.choke.threshold;
    if (meter < t.none) return 0;
    if (meter < t.mildWarning) {
      const ratio = (meter - t.none) / (t.mildWarning - t.none);
      return TUNING.choke.probabilityPerSec.mild.prob[0]
        + (TUNING.choke.probabilityPerSec.mild.prob[1] - TUNING.choke.probabilityPerSec.mild.prob[0]) * ratio;
    }
    if (meter < t.heavyWarning) {
      const ratio = (meter - t.mildWarning) / (t.heavyWarning - t.mildWarning);
      return TUNING.choke.probabilityPerSec.moderate.prob[0]
        + (TUNING.choke.probabilityPerSec.moderate.prob[1] - TUNING.choke.probabilityPerSec.moderate.prob[0]) * ratio;
    }
    const ratio = Math.min(1, (meter - t.heavyWarning) / (99 - t.heavyWarning));
    return TUNING.choke.probabilityPerSec.severe.prob[0]
      + (TUNING.choke.probabilityPerSec.severe.prob[1] - TUNING.choke.probabilityPerSec.severe.prob[0]) * ratio;
  }
}
